import { useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useBoundStore } from '@/store';
import { tokenStorage } from '@/lib/tokenStorage';
import { hasDatacenterAccess } from '@/utils/datacenter';
import { useUserDatacenters } from '@/hooks/useUserDatacenters';
import type { Region } from '@/constants/regions';

/**
 * Encapsulates the dashboard's region-switch flow so every entry point performs the
 * exact same cleanup:
 *
 * - validates the user has access to the target region
 * - persists the new region to the store (localStorage)
 * - clears the region-scoped ThoughtSpot SDK token (invalid for the new region)
 * - clears the React Query cache (prevents stale cross-region data)
 * - hard-navigates to the destination with `?datacenter=<region>` so `useDatacenterInit`
 *   re-initializes region, URLs, and everything else on load
 *
 * Used by both the legacy user-menu region picker and the unified-platform
 * TopNavigation region switcher.
 *
 * @returns changeRegion(region, targetUrl?) — `targetUrl` defaults to the current
 *   location (legacy same-origin switch); the unified-platform switcher passes the
 *   target region's host so the browser lands on the correct datacenter.
 */
export function useRegionChange(): (region: Region, targetUrl?: string) => void {
  const queryClient = useQueryClient();
  const selectedRegion = useBoundStore((state) => state.selectedRegion);
  const setSelectedRegion = useBoundStore((state) => state.setSelectedRegion);
  const { userDatacenters } = useUserDatacenters();

  // Ref (not state) so the re-entry guard survives without triggering a re-render;
  // the flag only needs to block rapid double-clicks before the page reload kicks in.
  const isChangingRegionRef = useRef(false);

  return useCallback(
    (region: Region, targetUrl?: string) => {
      // Prevent double-clicks during region transition
      if (isChangingRegionRef.current) {
        return;
      }

      // Validate user has access to the target region
      if (!hasDatacenterAccess(region, userDatacenters)) {
        console.error('[Region Switch] Access denied to region:', {
          requestedRegion: region,
          allowedRegions: userDatacenters,
        });
        return;
      }

      // Build the destination URL up front, before any cleanup, so we never leave the app
      // in a half-cleared state. `targetUrl` defaults to the current location (legacy path);
      // callers that pass one always pass a URL already validated by `new URL(...)`.
      const url = new URL(targetUrl ?? window.location.href);
      url.searchParams.set('datacenter', region);

      isChangingRegionRef.current = true;
      setSelectedRegion(region);

      // Clear SDK token from sessionStorage before reload
      // The token is region-specific and invalid for the new region
      tokenStorage.clearToken();
      // Clear React Query cache to prevent stale data from old region
      queryClient.clear();

      if (!import.meta.env.PROD) {
        console.log('[Region Switch] Switching region:', {
          from: selectedRegion,
          to: region,
          cacheCleared: true,
          localStorageUpdated: true,
        });
      }

      // Full page reload with the new datacenter query parameter.
      // On reload, useDatacenterInit will read the query param and initialize everything.
      window.location.href = url.toString();
    },
    [queryClient, selectedRegion, setSelectedRegion, userDatacenters],
  );
}
